import React from "react";
import PropTypes from "prop-types";
import {
  AlertTriangle,
  Button,
  Plus,
  Tabs,
  Tooltip,
} from "@opendate-app/setlist";
import {
  addDays,
  businessMinutes,
  clockLabel,
  drawerDayLabel,
  hoursLabel,
  minutesClock,
  shortName,
  wholeDollars,
  shiftColumnId,
  shiftDay,
} from "./scheduleFormatters";
import { ColumnHeader, cardStyle, sortShifts } from "./ScheduleGrid";
import { useStaffingScheduleContext } from "./StaffingScheduleContext";

const HOUR = 60;
const EMPTY_DAY = [16 * HOUR, 26 * HOUR];

const borderTop = { borderTop: "1px solid var(--sl-border-secondary)" };
const smallText = {
  fontSize: 12,
  lineHeight: "18px",
  color: "var(--sl-text-tertiary)",
};

const shiftSpan = (shift) => {
  const start = businessMinutes(shift.start_clock);
  let end = businessMinutes(shift.end_clock);
  if (end <= start) end += 24 * HOUR;
  return [start, end];
};

const shiftTag = (shift) => {
  if (!shift.staff_member_id) return "OPEN";
  if (shift.state === "draft") return "DRAFT";
  return shift.pending_change ? "EDITED" : null;
};

// A show's doors, show and end in business-day minutes; a time earlier than doors is the next morning.
const showTimes = (column) => {
  const doors = column.doors_clock && businessMinutes(column.doors_clock);
  return [
    ["Doors", column.doors_clock, "1px dashed var(--sl-border-brand)"],
    ["Show", column.show_clock, "2px solid var(--sl-fg-brand)"],
    ["End", column.end_clock, "1px dashed var(--sl-border-brand)"],
  ]
    .filter(([, clock]) => clock)
    .map(([name, clock, line]) => {
      let minutes = businessMinutes(clock);
      if (doors != null && minutes < doors) minutes += 24 * HOUR;
      return { name, clock, line, minutes };
    });
};

// The axis covers the day's shifts and show times, rounded out to whole hours.
const dayAxis = (shifts, columns) => {
  const points = [
    ...shifts.flatMap(shiftSpan),
    ...columns.flatMap((column) =>
      showTimes(column).map((time) => time.minutes),
    ),
  ];
  if (points.length === 0) return EMPTY_DAY;
  const start = Math.floor(Math.min(...points) / HOUR) * HOUR;
  const end = Math.ceil(Math.max(...points) / HOUR) * HOUR;
  return [start, Math.max(end, start + HOUR)];
};

const Markers = ({ columns, percent }) =>
  columns.flatMap((column) =>
    showTimes(column).map((time) => (
      <span
        key={`${column.id}:${time.name}`}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: percent(time.minutes),
          borderLeft: time.line,
          pointerEvents: "none",
        }}
      />
    )),
  );

const Track = ({ height, background, columns, percent, children }) => (
  <div style={{ ...borderTop, background, padding: "0 16px" }}>
    <div style={{ position: "relative", height }}>
      <Markers columns={columns} percent={percent} />
      {children}
    </div>
  </div>
);

Track.propTypes = {
  height: PropTypes.number.isRequired,
  background: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  percent: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const Label = ({ height, background, children }) => (
  <div
    className="d-flex align-items-center justify-content-between"
    style={{
      ...borderTop,
      height,
      boxSizing: "border-box",
      padding: "0 12px",
      gap: 8,
      position: "sticky",
      left: 0,
      zIndex: 2,
      background: background || "var(--sl-bg-primary)",
      borderRight: "1px solid var(--sl-border-secondary)",
      minWidth: 0,
    }}
  >
    {children}
  </div>
);

Label.propTypes = {
  height: PropTypes.number.isRequired,
  background: PropTypes.string,
  children: PropTypes.node,
};

const DayTimeline = ({
  columns,
  day,
  onDayChange,
  onOpenShift,
  onAddShift,
  narrow,
  cancellingEventId,
  onCancelShifts,
  changingTimesEventId,
  onChangeShowTimes,
}) => {
  const { schedule, permissions } = useStaffingScheduleContext();
  const dayColumns = columns.filter((column) => column.date === day);
  // A shift sits on its own work date, which can differ from its show's after a show moves.
  const visibleIds = new Set(columns.map((column) => column.id));
  const shiftsOn = (date) =>
    schedule.shifts.filter(
      (shift) =>
        shiftDay(shift) === date && visibleIds.has(shiftColumnId(shift)),
    );
  const dayShifts = shiftsOn(day);
  const [axisStart, axisEnd] = dayAxis(dayShifts, dayColumns);
  const span = axisEnd - axisStart;
  const percent = (minutes) => `${((minutes - axisStart) / span) * 100}%`;
  const hours = Array.from(
    { length: span / HOUR + 1 },
    (_, index) => axisStart + index * HOUR,
  );
  const labelWidth = narrow ? 128 : 220;
  const trackMin = narrow ? (span / HOUR) * 56 : 720;

  const days = Array.from({ length: 7 }, (_, index) => {
    const date = addDays(schedule.week.starts_on, index);
    const count = shiftsOn(date).length;
    return {
      value: date,
      label: drawerDayLabel(date),
      count: count || undefined,
    };
  });

  const dayTabs = (
    <div className="mb-3">
      <Tabs
        variant="button-border"
        items={days}
        value={day}
        onChange={onDayChange}
      />
    </div>
  );

  if (dayColumns.length === 0 && dayShifts.length === 0) {
    return (
      <>
        {dayTabs}
        <div
          style={{
            ...smallText,
            padding: 24,
            textAlign: "center",
            border: "1px solid var(--sl-border-secondary)",
            borderRadius: "var(--sl-radius-xl)",
            background: "var(--sl-bg-primary)",
          }}
        >
          No shows on {drawerDayLabel(day)}.
        </div>
      </>
    );
  }

  // Each shift's projected cost (overtime included), totalled by work date like the hours beside it.
  const pricedShifts = dayShifts.filter(
    (shift) => shift.projected_cost != null,
  );
  const dayCost = pricedShifts.length
    ? pricedShifts.reduce((sum, shift) => sum + Number(shift.projected_cost), 0)
    : null;

  const addButton = (row) =>
    permissions.manageSchedule &&
    row.can_manage &&
    dayColumns[0] && (
      <Button
        variant="tertiary"
        size="sm"
        icon={Plus}
        aria-label={`Add ${row.name} shift`}
        onClick={() => onAddShift(row, dayColumns[0])}
      >
        {narrow ? null : "Add"}
      </Button>
    );

  return (
    <>
      {dayTabs}
      <div
        style={{
          border: "1px solid var(--sl-border-secondary)",
          borderRadius: "var(--sl-radius-xl)",
          background: "var(--sl-bg-primary)",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${labelWidth}px minmax(${trackMin}px, 1fr)`,
            minWidth: labelWidth + trackMin + 32,
          }}
        >
          <div
            style={{
              ...smallText,
              height: 32,
              padding: "6px 12px",
              boxSizing: "border-box",
              position: "sticky",
              left: 0,
              zIndex: 2,
              background: "var(--sl-bg-primary)",
            }}
          >
            {drawerDayLabel(day)}
          </div>
          <div style={{ padding: "0 16px" }}>
            <div style={{ position: "relative", height: 32 }}>
              {hours.map((minutes) => (
                <span
                  key={minutes}
                  style={{
                    ...smallText,
                    position: "absolute",
                    top: 6,
                    left: percent(minutes),
                    transform: "translateX(-50%)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {clockLabel(minutesClock(minutes))}
                </span>
              ))}
            </div>
          </div>

          {dayColumns.map((column) => {
            const times = showTimes(column);
            const doors = times.find((time) => time.name === "Doors");
            const end = times.find((time) => time.name === "End");
            return (
              <React.Fragment key={column.id}>
                <div style={{ ...borderTop, gridColumn: "1 / -1" }}>
                  <div
                    style={{
                      position: "sticky",
                      left: 0,
                      width: narrow ? 340 : 480,
                      maxWidth: "calc(100vw - 64px)",
                    }}
                  >
                    <ColumnHeader
                      column={column}
                      shifts={schedule.shifts.filter(
                        (shift) => shiftColumnId(shift) === column.id,
                      )}
                      cancelling={cancellingEventId != null}
                      onCancelShifts={onCancelShifts}
                      changingTimes={changingTimesEventId != null}
                      onChangeShowTimes={onChangeShowTimes}
                    />
                  </div>
                </div>
                {times.length > 0 && (
                  <>
                    <Label height={56}>
                      <span style={smallText}>Show times</span>
                    </Label>
                    <Track height={56} columns={dayColumns} percent={percent}>
                      {doors && end && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            top: 8,
                            height: 16,
                            left: percent(doors.minutes),
                            width: `${((end.minutes - doors.minutes) / span) * 100}%`,
                            borderRadius: 4,
                            background: "var(--sl-bg-brand-primary)",
                            border: "1px solid var(--sl-border-brand)",
                          }}
                        />
                      )}
                      {times.map((time) => (
                        <span
                          key={time.name}
                          style={{
                            position: "absolute",
                            top: 30,
                            left: percent(time.minutes),
                            transform: "translateX(-50%)",
                            padding: "0 4px",
                            fontSize: 12,
                            lineHeight: "18px",
                            fontWeight: time.name === "Show" ? 600 : 400,
                            color: "var(--sl-fg-brand)",
                            background: "var(--sl-bg-primary)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {time.name} {clockLabel(time.clock)}
                        </span>
                      ))}
                    </Track>
                  </>
                )}
              </React.Fragment>
            );
          })}

          {schedule.rows.map((row) => {
            const laneShifts = sortShifts(
              dayShifts.filter((shift) => shift.staff_position_id === row.id),
            );
            return (
              <React.Fragment key={row.id}>
                <Label height={40} background="var(--sl-bg-secondary)">
                  <span
                    className="d-flex align-items-center"
                    style={{
                      gap: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: row.color_hex || "var(--sl-fg-brand)",
                        flexShrink: 0,
                      }}
                    />
                    {/* On a phone the name runs over the gray header track instead of truncating. */}
                    <span
                      style={
                        narrow
                          ? { whiteSpace: "nowrap" }
                          : {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }
                      }
                    >
                      {row.name}
                    </span>
                    {narrow && addButton(row)}
                  </span>
                  {!narrow && addButton(row)}
                </Label>
                <Track
                  height={40}
                  background="var(--sl-bg-secondary)"
                  columns={dayColumns}
                  percent={percent}
                />
                {laneShifts.length === 0 && (
                  <>
                    <Label height={44}>
                      <span style={smallText}>No shifts</span>
                    </Label>
                    <Track height={44} columns={dayColumns} percent={percent} />
                  </>
                )}
                {laneShifts.map((shift) => {
                  const [start, end] = shiftSpan(shift);
                  const open = !shift.staff_member_id;
                  const tag = shiftTag(shift);
                  return (
                    <React.Fragment key={shift.id}>
                      <Label height={44}>
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: open
                              ? "var(--sl-text-warning)"
                              : "var(--sl-text-primary)",
                          }}
                        >
                          {open
                            ? "Open shift"
                            : shortName(
                                shift.staff_member_first_name,
                                shift.staff_member_last_name,
                              )}
                        </span>
                      </Label>
                      <Track height={44} columns={dayColumns} percent={percent}>
                        <button
                          type="button"
                          data-shift-bar={shift.id}
                          onClick={() => onOpenShift(shift)}
                          className="d-flex align-items-center justify-content-between"
                          style={{
                            ...cardStyle(shift),
                            position: "absolute",
                            top: 6,
                            height: 32,
                            left: percent(start),
                            width: `${((end - start) / span) * 100}%`,
                            boxSizing: "border-box",
                            borderRadius: "var(--sl-radius-md)",
                            padding: "0 8px",
                            gap: 6,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            color: open
                              ? "var(--sl-text-warning)"
                              : "var(--sl-text-primary)",
                          }}
                        >
                          <span>
                            {clockLabel(shift.start_clock)}–
                            {clockLabel(shift.end_clock)}
                          </span>
                          <span
                            className="d-flex align-items-center"
                            style={{ gap: 6 }}
                          >
                            {shift.warnings.length > 0 && (
                              <Tooltip title={shift.warnings.join("\n")}>
                                <AlertTriangle
                                  width={16}
                                  height={16}
                                  style={{ color: "var(--sl-fg-warning)" }}
                                  aria-label="Has warnings"
                                />
                              </Tooltip>
                            )}
                            {tag && (
                              <span
                                style={{
                                  fontSize: 11,
                                  lineHeight: "16px",
                                  fontWeight: 600,
                                  letterSpacing: "0.04em",
                                  color: open
                                    ? "var(--sl-text-warning)"
                                    : "var(--sl-text-quaternary)",
                                }}
                              >
                                {tag}
                              </span>
                            )}
                          </span>
                        </button>
                      </Track>
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}

          <div
            style={{
              ...borderTop,
              ...smallText,
              padding: "12px 16px",
              position: "sticky",
              left: 0,
              zIndex: 2,
              background: "var(--sl-bg-secondary)",
              color: "var(--sl-text-quaternary)",
            }}
          >
            Scheduled
          </div>
          <div
            className="d-flex align-items-center"
            style={{
              ...borderTop,
              padding: "12px 16px",
              gap: 12,
              background: "var(--sl-bg-secondary)",
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--sl-text-primary)",
              }}
            >
              {hoursLabel(
                dayShifts.reduce((sum, shift) => sum + shift.paid_minutes, 0),
              )}{" "}
              h
            </span>
            {permissions.readWages && dayCost != null && (
              <span style={smallText}>{wholeDollars(dayCost)}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

DayTimeline.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  day: PropTypes.string.isRequired,
  onDayChange: PropTypes.func.isRequired,
  onOpenShift: PropTypes.func.isRequired,
  onAddShift: PropTypes.func.isRequired,
  narrow: PropTypes.bool,
  cancellingEventId: PropTypes.number,
  onCancelShifts: PropTypes.func.isRequired,
  changingTimesEventId: PropTypes.number,
  onChangeShowTimes: PropTypes.func.isRequired,
};

export default DayTimeline;
